"use client";

import React, { useMemo } from "react";
import * as THREE from "three";
import { lakeShape, MAP_HALF, terrainHeight, TOWN_PADS, WATERLINE } from "./empire-terrain";

const SPAWNS: Array<[number, number]> = TOWN_PADS.map((p) => [p.x, p.z]);

function dry(x: number, z: number, seed: number, biome?: string | null) {
  if (Math.abs(x) > MAP_HALF - 2 || Math.abs(z) > MAP_HALF - 2) return false;
  return terrainHeight(x, z, seed, biome) > WATERLINE + 0.28;
}

type Span = { a: [number, number]; b: [number, number] };

function segmentDry(
  x: number,
  z: number,
  nx: number,
  nz: number,
  seed: number,
  biome?: string | null
) {
  if (!dry(nx, nz, seed, biome)) return false;
  for (let t = 0.2; t <= 0.8; t += 0.2) {
    if (!dry(x + (nx - x) * t, z + (nz - z) * t, seed, biome)) return false;
  }
  return true;
}

/** Stay on dry ground. When the straight step would touch water, walk the bank. */
function walk(
  ax: number,
  az: number,
  bx: number,
  bz: number,
  seed: number,
  biome: string | null | undefined,
  _bridges: Span[]
) {
  const pts: Array<[number, number]> = [[ax, az]];
  let x = ax;
  let z = az;
  let side = 1;
  let stuck = 0;
  for (let i = 0; i < 96; i++) {
    const dx = bx - x;
    const dz = bz - z;
    const d = Math.hypot(dx, dz);
    if (d < 1.5) break;
    const step = 1.15;
    const ux = dx / d;
    const uz = dz / d;
    let nx = x + ux * step;
    let nz = z + uz * step;
    if (!segmentDry(x, z, nx, nz, seed, biome)) {
      const px = -uz;
      const pz = ux;
      const a: [number, number] = [x + px * side * step, z + pz * side * step];
      const b: [number, number] = [x - px * side * step, z - pz * side * step];
      const aOk = segmentDry(x, z, a[0], a[1], seed, biome);
      const bOk = segmentDry(x, z, b[0], b[1], seed, biome);
      if (aOk && bOk) {
        const da = Math.hypot(bx - a[0], bz - a[1]);
        const db = Math.hypot(bx - b[0], bz - b[1]);
        if (db + 0.4 < da) {
          side = -side;
          nx = b[0];
          nz = b[1];
        } else {
          nx = a[0];
          nz = a[1];
        }
      } else if (aOk) {
        nx = a[0];
        nz = a[1];
      } else if (bOk) {
        side = -side;
        nx = b[0];
        nz = b[1];
      } else {
        stuck += 1;
        side = -side;
        if (stuck > 8) break;
        continue;
      }
    }
    if (Math.hypot(nx - x, nz - z) < 0.2) {
      stuck += 1;
      if (stuck > 8) break;
      continue;
    }
    stuck = 0;
    x = nx;
    z = nz;
    pts.push([x, z]);
  }
  if (dry(bx, bz, seed, biome) && segmentDry(x, z, bx, bz, seed, biome)) pts.push([bx, bz]);
  return smoothLine(pts, (px, pz) => dry(px, pz, seed, biome));
}

function bridgeGeo(spans: Span[], seed: number, biome?: string | null) {
  const pos: number[] = [];
  const idx: number[] = [];
  const pushBox = (cx: number, cy: number, cz: number, sx: number, sy: number, sz: number, yaw: number) => {
    const hx = sx / 2;
    const hy = sy / 2;
    const hz = sz / 2;
    const c = Math.cos(yaw);
    const s = Math.sin(yaw);
    const corners = [
      [-hx, -hy, -hz],
      [hx, -hy, -hz],
      [hx, hy, -hz],
      [-hx, hy, -hz],
      [-hx, -hy, hz],
      [hx, -hy, hz],
      [hx, hy, hz],
      [-hx, hy, hz],
    ].map(([x, y, z]) => [cx + x * c - z * s, cy + y, cz + x * s + z * c]);
    const base = pos.length / 3;
    for (const p of corners) pos.push(p[0], p[1], p[2]);
    const faces = [
      [0, 1, 2, 3],
      [4, 6, 5, 7],
      [0, 4, 5, 1],
      [2, 6, 7, 3],
      [0, 3, 7, 4],
      [1, 5, 6, 2],
    ];
    for (const f of faces) {
      idx.push(base + f[0], base + f[1], base + f[2], base + f[0], base + f[2], base + f[3]);
    }
  };
  for (const span of spans) {
    const dx = span.b[0] - span.a[0];
    const dz = span.b[1] - span.a[1];
    const len = Math.hypot(dx, dz) || 1;
    const yaw = Math.atan2(dx, dz);
    const ha = terrainHeight(span.a[0], span.a[1], seed, biome);
    const hb = terrainHeight(span.b[0], span.b[1], seed, biome);
    const y = Math.max(ha, hb, WATERLINE + 0.42) + 0.12;
    const cx = (span.a[0] + span.b[0]) / 2;
    const cz = (span.a[1] + span.b[1]) / 2;
    pushBox(cx, y, cz, 1.15, 0.1, len + 0.4, yaw);
    const rx = (-dz / len) * 0.52;
    const rz = (dx / len) * 0.52;
    pushBox(cx + rx, y + 0.28, cz + rz, 0.08, 0.36, len + 0.2, yaw);
    pushBox(cx - rx, y + 0.28, cz - rz, 0.08, 0.36, len + 0.2, yaw);
  }
  const g = new THREE.BufferGeometry();
  if (!pos.length) {
    g.setAttribute("position", new THREE.Float32BufferAttribute([0, 0, 0], 3));
    return g;
  }
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

/** Fitted flagstones with mortar, fading into the grass at the shoulders. */
function roadTexture() {
  const w = 128;
  const h = 512;
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d");
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  if (!ctx) return tex;
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = "#6a6358";
  ctx.fillRect(0, 0, w, h);
  const stones = ["#d9d0c0", "#c4baa8", "#e4dccb", "#b3a894", "#cec4b2", "#a89d8c", "#ddd4c4"];
  let y = 1;
  let row = 0;
  while (y < h) {
    const sh = 16 + ((row * 3) % 5) * 3;
    let x = (row % 2) * 18 - 6;
    let col = 0;
    while (x < w) {
      const sw = 20 + ((row * 5 + col * 11) % 16);
      const right = Math.min(w, x + sw);
      ctx.fillStyle = stones[(row * 3 + col * 2) % stones.length];
      ctx.fillRect(x + 1.5, y + 1.5, Math.max(2, right - x - 3), sh - 3);
      ctx.fillStyle = "rgba(90, 82, 70, 0.22)";
      ctx.fillRect(x + 5, y + 5, 4, 2);
      x = right;
      col++;
    }
    y += sh;
    row++;
  }
  const fade = ctx.createLinearGradient(0, 0, w, 0);
  fade.addColorStop(0, "rgba(0,0,0,1)");
  fade.addColorStop(0.1, "rgba(0,0,0,0.15)");
  fade.addColorStop(0.2, "rgba(0,0,0,0)");
  fade.addColorStop(0.8, "rgba(0,0,0,0)");
  fade.addColorStop(0.9, "rgba(0,0,0,0.15)");
  fade.addColorStop(1, "rgba(0,0,0,1)");
  ctx.globalCompositeOperation = "destination-out";
  ctx.fillStyle = fade;
  ctx.fillRect(0, 0, w, h);
  ctx.globalCompositeOperation = "source-over";
  tex.needsUpdate = true;
  return tex;
}

function smoothLine(
  pts: Array<[number, number]>,
  ok: (x: number, z: number) => boolean
): Array<[number, number]> {
  if (pts.length < 3) return pts;
  let cur = pts;
  for (let pass = 0; pass < 2; pass++) {
    const next: Array<[number, number]> = [cur[0]];
    for (let i = 1; i < cur.length - 1; i++) {
      const a = cur[i - 1];
      const b = cur[i];
      const c = cur[i + 1];
      if (Math.hypot(b[0] - a[0], b[1] - a[1]) > 2.2 || Math.hypot(c[0] - b[0], c[1] - b[1]) > 2.2) {
        next.push(b);
        continue;
      }
      const px = (a[0] + b[0] * 2 + c[0]) / 4;
      const pz = (a[1] + b[1] * 2 + c[1]) / 4;
      next.push(ok(px, pz) ? [px, pz] : b);
    }
    next.push(cur[cur.length - 1]);
    cur = next;
  }
  return cur;
}

function ribbon(
  lines: Array<{ pts: Array<[number, number]>; width: number }>,
  seed: number,
  biome?: string | null,
  lift = 0.045
) {
  const pos: number[] = [];
  const uv: number[] = [];
  const col: number[] = [];
  const idx: number[] = [];
  let base = 0;
  for (const line of lines) {
    if (line.pts.length < 2) continue;
    let dist = 0;
    const half = line.width * 0.5;
    for (let i = 0; i < line.pts.length; i++) {
      const [x, z] = line.pts[i];
      const prev = line.pts[Math.max(0, i - 1)];
      const next = line.pts[Math.min(line.pts.length - 1, i + 1)];
      let dx = next[0] - prev[0];
      let dz = next[1] - prev[1];
      const len = Math.hypot(dx, dz) || 1;
      dx /= len;
      dz /= len;
      const px = -dz;
      const pz = dx;
      const y = terrainHeight(x, z, seed, biome) + lift;
      if (i > 0) dist += Math.hypot(x - line.pts[i - 1][0], z - line.pts[i - 1][1]);
      const v = dist * 0.55;
      const row = [
        [x + px * half, y, z + pz * half, 0, v, 0.82, 0.8, 0.74],
        [x + px * half * 0.34, y + 0.003, z + pz * half * 0.34, 0.28, v, 1, 0.98, 0.94],
        [x - px * half * 0.34, y + 0.003, z - pz * half * 0.34, 0.72, v, 1, 0.98, 0.94],
        [x - px * half, y, z - pz * half, 1, v, 0.82, 0.8, 0.74],
      ];
      for (const p of row) {
        pos.push(p[0], p[1], p[2]);
        uv.push(p[3], p[4]);
        col.push(p[5], p[6], p[7]);
      }
      const prevPt = i > 0 ? line.pts[i - 1] : null;
      const gap = prevPt ? Math.hypot(x - prevPt[0], z - prevPt[1]) : 0;
      const prevY = prevPt ? terrainHeight(prevPt[0], prevPt[1], seed, biome) : y;
      const midDry =
        !!prevPt &&
        dry((x + prevPt[0]) / 2, (z + prevPt[1]) / 2, seed, biome) &&
        dry(x, z, seed, biome);
      if (i > 0 && gap < 2.3 && midDry && Math.abs(y - prevY) < 0.55) {
        const a = base + (i - 1) * 4;
        const b = base + i * 4;
        for (let k = 0; k < 3; k++) {
          idx.push(a + k, b + k, a + k + 1, a + k + 1, b + k, b + k + 1);
        }
      }
    }
    base += line.pts.length * 4;
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

/** Dirt tracks baked into the ground, plus bridge spans where a track crosses water. */
export function landRoutes(
  seed: number,
  biome?: string | null
): { lines: Array<Array<[number, number]>>; spans: Span[] } {
  const L = lakeShape(seed);
  const lines: Array<Array<[number, number]>> = [];
  const spans: Span[] = [];
  for (let i = 0; i < SPAWNS.length; i++) {
    const [x, z] = SPAWNS[i];
    const away = Math.atan2(z - L.z, x - L.x);
    const ox = x + Math.cos(away) * 6.5;
    const oz = z + Math.sin(away) * 6.5;
    if (dry(ox, oz, seed, biome)) lines.push(walk(x, z, ox, oz, seed, biome, spans));
  }
  lines.push(walk(SPAWNS[0][0], SPAWNS[0][1], SPAWNS[1][0], SPAWNS[1][1], seed, biome, spans));
  lines.push(walk(SPAWNS[1][0], SPAWNS[1][1], SPAWNS[2][0], SPAWNS[2][1], seed, biome, spans));
  lines.push(walk(SPAWNS[2][0], SPAWNS[2][1], SPAWNS[3][0], SPAWNS[3][1], seed, biome, spans));
  lines.push(walk(SPAWNS[3][0], SPAWNS[3][1], SPAWNS[0][0], SPAWNS[0][1], seed, biome, spans));
  return { lines, spans };
}

/** Trees standing on both sides of a path, clear of the town pads and the water. */
export function avenueTrees(
  seed: number,
  biome?: string | null
): Array<{ x: number; z: number; s: number; hue: number }> {
  const { lines } = landRoutes(seed, biome);
  const out: Array<{ x: number; z: number; s: number; hue: number }> = [];
  lines.forEach((line, li) => {
    for (let i = 2; i < line.length - 2; i += 3) {
      const [x, z] = line[i];
      const prev = line[i - 1];
      const dx = x - prev[0];
      const dz = z - prev[1];
      const len = Math.hypot(dx, dz) || 1;
      const px = -dz / len;
      const pz = dx / len;
      for (const side of [-1, 1]) {
        const tx = x + px * side * 1.55;
        const tz = z + pz * side * 1.55;
        if (!dry(tx, tz, seed, biome)) continue;
        let nearTown = false;
        for (const [sx, sz] of SPAWNS) {
          if (Math.hypot(tx - sx, tz - sz) < 8) nearTown = true;
        }
        if (nearTown) continue;
        out.push({
          x: tx,
          z: tz,
          s: 0.95 + ((i + li) % 3) * 0.12,
          hue: ((i * 0.13 + li + side) % 1 + 1) % 1,
        });
      }
    }
  });
  return out.slice(0, 180);
}

export function RoadNet({ seed, biome }: { seed: number; biome?: string | null }) {
  const routes = useMemo(() => landRoutes(seed, biome), [seed, biome]);
  const bridges = useMemo(() => bridgeGeo(routes.spans, seed, biome), [routes, seed, biome]);
  const paths = useMemo(
    () => ribbon(routes.lines.map((pts) => ({ pts, width: 1.7 })), seed, biome, 0.012),
    [routes, seed, biome]
  );
  const dirt = useMemo(() => roadTexture(), []);
  return (
    <group>
      <mesh geometry={paths} receiveShadow>
        <meshStandardMaterial
          map={dirt}
          vertexColors
          transparent
          depthWrite={false}
          roughness={0.94}
          polygonOffset
          polygonOffsetFactor={-2}
          polygonOffsetUnits={-2}
        />
      </mesh>
      <mesh geometry={bridges} castShadow receiveShadow>
        <meshStandardMaterial color="#6e4e32" roughness={0.86} />
      </mesh>
    </group>
  );
}
